import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Graph from "react-graph-vis";
// Import Supabase client and helpers
import { supabase, getNodeGraphData, saveNodeGraphData, subscribeToNodeGraphChanges } from './supabase';
import AdminPanel from './components/AdminPanel';
import NodeGraph from './components/NodeGraph';

// Test connection function to help debug Supabase issues
const testConnection = async () => {
  try {
    console.log('Testing Supabase connection...');
    
    // Just check if we can connect to Supabase without inserting records
    const { data, error } = await supabase
      .from('node_graphs')
      .select('count')
      .limit(1);
    
    if (error) throw error;
    
    console.log('Connection test successful!');
    const timestamp = new Date().toISOString();
    return { success: true, timestamp };
  } catch (error) {
    console.error('Connection test failed:', error);
    return { success: false, error: error.message };
  }
};

// Run test on page load
testConnection();

function App() {
  const [nodesData, setNodesData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ saving: false, success: false, error: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  
  // Admin password - in a real app, this would be securely stored server-side
  const ADMIN_PASSWORD = 'admin123';

  // Keyboard shortcut handler for admin panel (Command+A)
  const handleKeyDown = useCallback((event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'a') {
      event.preventDefault(); // Prevent default browser behavior
      setShowPasswordPrompt(true);
    }
  }, []);

  // Add keyboard event listener
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Handle password verification
  const verifyPassword = () => {
    if (password === ADMIN_PASSWORD) {
      setIsAdminPanelOpen(true);
      setShowPasswordPrompt(false);
      setPassword('');
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  // Function to initialize default data in Supabase
  const initializeDefaultData = async () => {
    try {
      console.log('Checking for existing data before initialization...');
      
      // Protection check - see if data already exists before creating default data
      const { data: existing } = await supabase
        .from('node_graphs')
        .select('graph_id')
        .eq('graph_id', 'data')
        .single();
        
      if (existing) {
        console.log('Found existing data, skipping default initialization');
        // Fetch the existing data instead of creating default
        const existingData = await getNodeGraphData();
        if (existingData && existingData.nodes && existingData.links) {
          console.log('Successfully loaded existing data:', existingData.nodes.length, 'nodes');
          setNodesData(existingData);
          return;
        }
      }
      
      console.log('No existing data found. Creating default data in Supabase...');
      const defaultData = {
        nodes: [
          // Primary nodes (purple)
          { id: '1', name: 'Node 1', content: 'Primary node', type: 'primary' },
          { id: '2', name: 'Node 2', content: 'Primary node', type: 'primary' },
          { id: '3', name: 'Node 3', content: 'Primary node', type: 'primary' },
          { id: '4', name: 'Node 4', content: 'Primary node', type: 'primary' },
          { id: '5', name: 'Node 5', content: 'Primary node', type: 'primary' },
          { id: '6', name: 'Node 6', content: 'Primary node', type: 'primary' },
          { id: '7', name: 'Node 7', content: 'Primary node', type: 'primary' },
          { id: '8', name: 'Node 8', content: 'Primary node', type: 'primary' },
          { id: '9', name: 'Node 9', content: 'Primary node', type: 'primary' },
          { id: '10', name: 'Node 10', content: 'Primary node', type: 'primary' },
          
          // Secondary nodes (black)
          { id: '11', name: 'Node 11', content: 'Secondary node', type: 'secondary' },
          { id: '12', name: 'Node 12', content: 'Secondary node', type: 'secondary' },
          { id: '13', name: 'Node 13', content: 'Secondary node', type: 'secondary' },
        ],
        links: [
          // Connections between nodes
          { source: '1', target: '2' },
          { source: '1', target: '3' },
          { source: '2', target: '4' },
          { source: '3', target: '5' },
          { source: '4', target: '6' },
          { source: '5', target: '7' },
          { source: '6', target: '8' },
          { source: '7', target: '9' },
          { source: '8', target: '10' },
          { source: '9', target: '11' },
          { source: '10', target: '12' },
          { source: '11', target: '13' },
        ]
      };
      
      const success = await saveNodeGraphData(defaultData);
      if (success) {
        console.log('Default data initialized in Supabase');
        setNodesData(defaultData);
      } else {
        throw new Error('Failed to save default data');
      }
    } catch (error) {
      console.error('Error initializing default data:', error);
      // Fall back to using default data locally
      const defaultData = {
        nodes: [
          { id: '1', name: 'Node 1', displayLabel: 'Primary 1', content: 'Primary node', type: 'primary' },
          { id: '2', name: 'Node 2', displayLabel: 'Primary 2', content: 'Primary node', type: 'primary' },
          { id: '3', name: 'Node 3', displayLabel: 'Primary 3', content: 'Primary node', type: 'primary' },
          { id: '4', name: 'Node 4', displayLabel: 'Secondary 1', content: 'Secondary node', type: 'secondary' },
          { id: '5', name: 'Node 5', displayLabel: 'Tertiary 1', content: 'Tertiary node', type: 'tertiary' },
          { id: '6', name: 'Node 6', displayLabel: 'Tertiary 2', content: 'Tertiary node', type: 'tertiary' },
        ],
        links: [
          { source: '1', target: '2' },
          { source: '2', target: '3' },
          { source: '3', target: '4' },
          { source: '3', target: '5' },
          { source: '5', target: '6' },
        ]
      };
      setNodesData(defaultData);
    } finally {
      setIsLoading(false);
    }
  };

  // Load data from Supabase
  useEffect(() => {
    setIsLoading(true);
    let unsubscribe = null;

    const fetchData = async () => {
      try {
        console.log('Starting data load process from Supabase...');
        
        // Get data from Supabase using our helper function
        const data = await getNodeGraphData();
        
        if (data && data.nodes && data.links) {
          console.log('Data loaded successfully from Supabase:', data);
          console.log('Nodes data loaded:', data.nodes.length, 'nodes found');
          console.log('First few nodes:', data.nodes.slice(0, 3));
          console.log('Links data loaded:', data.links.length, 'links found');
          setNodesData(data);

          // Set up real-time subscription for updates
          console.log('Setting up real-time subscriptions for data updates...');
          unsubscribe = subscribeToNodeGraphChanges((newData) => {
            if (newData && newData.nodes && newData.links) {
              console.log('Real-time update received from Supabase:', newData);
              setNodesData(newData);
            }
          });
        } else {
          console.log('No valid data found in Supabase, checking if we should create default data');
          
          // Additional protection - double check if data exists before initializing default
          const { data: doubleCheck } = await supabase
            .from('node_graphs')
            .select('graph_id')
            .eq('graph_id', 'data')
            .single();
          
          if (doubleCheck) {
            console.log('Found data during double check, trying to load again');
            // Data exists but might be malformed, try to load it anyway
            const retryData = await getNodeGraphData();
            setNodesData(retryData || { nodes: [], links: [] });
          } else {
            // Only initialize default data if truly nothing exists
            console.log('Confirmed no data exists, creating default data');
            initializeDefaultData();
          }
          return; // Exit early
        }
      } catch (error) {
        console.error('Error loading data:', error);
        console.log('Using default data as fallback');
        const defaultData = {
          nodes: [
            { id: '1', name: 'Node 1', displayLabel: 'Primary 1', content: 'Primary node', type: 'primary' },
            { id: '2', name: 'Node 2', displayLabel: 'Primary 2', content: 'Primary node', type: 'primary' },
            { id: '3', name: 'Node 3', displayLabel: 'Primary 3', content: 'Primary node', type: 'primary' },
            { id: '4', name: 'Node 4', displayLabel: 'Secondary 1', content: 'Secondary node', type: 'secondary' },
            { id: '5', name: 'Node 5', displayLabel: 'Secondary 2', content: 'Secondary node', type: 'secondary' },
          ],
          links: [
            { source: '1', target: '2' },
            { source: '2', target: '3' },
            { source: '3', target: '4' },
            { source: '4', target: '5' }
          ]
        };
        setNodesData(defaultData);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle data save
  const handleSaveData = async (data) => {
    console.log('Starting save process with data:', data);
    setSaveStatus({ saving: true, success: false, error: null });
    
    try {
      // Clean data to remove circular references
      const cleanData = {
        nodes: data.nodes.map(node => ({
          id: node.id,
          name: node.name || '',
          content: node.content || '',
          type: node.type || 'primary'
        })),
        links: data.links.map(link => ({
          source: typeof link.source === 'object' ? link.source.id : link.source,
          target: typeof link.target === 'object' ? link.target.id : link.target
        }))
      };
      
      console.log('Saving data to Supabase:', cleanData);
      const success = await saveNodeGraphData(cleanData);
      
      if (success) {
        console.log('Data saved successfully to Supabase');
        setSaveStatus({ saving: false, success: true, error: null });
      } else {
        throw new Error('Failed to save data to Supabase');
      }
      
      // Reset success message after 3 seconds
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSaveStatus(prevStatus => {
          if (prevStatus.success) {
            return { saving: false, success: false, error: null };
          }
          return prevStatus;
        });
      }, 3000);
    } catch (error) {
      console.error('Error saving data to Supabase:', error);
      setSaveStatus({ saving: false, success: false, error: `Failed to save: ${error.message}` });
    }
  };

  // Custom loading animation state
  const [loadingText, setLoadingText] = useState('');
  const [currentWord, setCurrentWord] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [showPeriod, setShowPeriod] = useState(true);

  // Effect to handle typing animation
  useEffect(() => {
    if (!isLoading) return;
    
    const phrases = ['Signal', 'Sequence', 'Render', 'Repeat'];
    const currentPhrase = phrases[currentWord];
    
    // Typing effect
    if (charIndex <= currentPhrase.length) {
      const typingTimer = setTimeout(() => {
        setLoadingText(currentPhrase.substring(0, charIndex));
        setCharIndex(charIndex + 1);
      }, 80); // Type each character with 80ms delay
      
      return () => clearTimeout(typingTimer);
    } 
    // Complete word and pause
    else if (charIndex === currentPhrase.length + 1) {
      // Show the complete word
      setLoadingText(currentPhrase);
      
      // Move to next word after a short delay
      const nextWordTimer = setTimeout(() => {
        setCurrentWord((currentWord + 1) % phrases.length);
        setCharIndex(0);
        setLoadingText('');
      }, 800);
      
      return () => clearTimeout(nextWordTimer);
    }
  }, [isLoading, currentWord, charIndex, showPeriod]);

  if (isLoading) {
    const phrases = ['Signal', 'Sequence', 'Render', 'Repeat'];
    const currentPhrase = phrases[currentWord];
    
    return (
      <div className="loading-container">
        <div className="flower-loader">✿</div>
        <p className="loading-text">
          {loadingText}
        </p>
      </div>
    );
  }

  if (!nodesData) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>Failed to load data. Please refresh the page.</p>
      </div>
    );
  }

  // Filter nodes and links based on search term
  const filteredNodes = nodesData.nodes.filter(node => 
    node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (node.content && node.content.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredLinks = nodesData.links.filter(link => {
    const sourceNode = nodesData.nodes.find(n => n.id === (link.source.id || link.source));
    const targetNode = nodesData.nodes.find(n => n.id === (link.target.id || link.target));
    return (sourceNode && filteredNodes.includes(sourceNode)) && (targetNode && filteredNodes.includes(targetNode));
  });

  return (
    <div className="app-container">

      <div className="main-content">
        <NodeGraph data={{ nodes: filteredNodes, links: filteredLinks }} searchTerm={searchTerm} />
        <div className="search-container">
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Password prompt modal */}
      {showPasswordPrompt && (
        <div className="admin-panel-overlay">
          <div className="password-prompt">
            <h3>Enter Admin Password</h3>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && verifyPassword()}
              className={passwordError ? 'password-input error' : 'password-input'}
              placeholder="Password"
              autoFocus
            />
            {passwordError && <p className="password-error">Incorrect password</p>}
            <div className="password-actions">
              <button onClick={() => {
                setShowPasswordPrompt(false);
                setPassword('');
                setPasswordError(false);
              }}>Cancel</button>
              <button onClick={verifyPassword}>Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* Admin panel */}
      {isAdminPanelOpen && (
        <div className="admin-panel-overlay">
          <div className="admin-panel-wrapper">
            <AdminPanel
              data={nodesData}
              onSave={handleSaveData}
              onClose={() => setIsAdminPanelOpen(false)}
            />
          </div>
        </div>
      )}

      {saveStatus.saving && (
        <div className="save-status">
          <div className="save-spinner"></div>
          <p>Saving...</p>
        </div>
      )}

      {saveStatus.success && (
        <div className="save-status success">
          <p>Saved successfully!</p>
        </div>
      )}

      {saveStatus.error && (
        <div className="save-status error">
          <p>Error: {saveStatus.error}</p>
        </div>
      )}
    </div>
  );
}

export default App;
